import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, Box, Typography, Button, Chip } from "@mui/material";
import { Star } from "@mui/icons-material";
import RatingCategoryGrid from "@/components/ratings/RatingCategoryGrid";
import type { Company } from "@/types";

interface CompanyCardProps {
  company: Company;
}

export default function CompanyCard({ company }: CompanyCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        cursor: "pointer",
        "&:hover": { transform: "translateY(-2px)" },
        transition: "all 0.2s ease",
      }}
      onClick={() => navigate(`/companies/${company.id}`)}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              {company.displayName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              OIB: {company.oib}
            </Typography>
          </Box>
          {company.overallRating && (
            <Chip
              icon={<Star sx={{ fontSize: 16 }} />}
              label={company.overallRating.toFixed(1)}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {company.averageRatings && (
          <RatingCategoryGrid ratings={company.averageRatings} gridSx={{ mt: 1 }} />
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/companies/${company.id}`);
            }}
          >
            {t("company.viewProfile")}
          </Button>
          <Button size="small" variant="contained" fullWidth>
            {t("company.readReviews")} ({company.reviewCount ?? 0})
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
